import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Image as ImageIcon, Upload, Loader2, Trash2, FilePlus2, Quote, ArrowUp, ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FiguresPanel({
  userId, projectId, chapters = [], refreshKey = 0, onInserted, onAssetsChanged,
  figureWord = 'Figure',
}) {
  const [figures, setFigures] = useState([]);
  const [caption, setCaption] = useState('');
  const [sourceNote, setSourceNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const [targets, setTargets] = useState({});
  const [lastTarget, setLastTarget] = useState('');
  const targetFor = (id) => targets[id] ?? lastTarget ?? '';
  const pickTarget = (id, value) => {
    setTargets((t) => ({ ...t, [id]: value }));
    setLastTarget(value);
  };

  const load = useCallback(async () => {
    try {
      setFigures(await thesisApi.listFigures(userId, projectId));
    } catch (e) {
      console.error(e);
    }
  }, [userId, projectId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fig = await thesisApi.uploadFigure(userId, projectId, file, caption, sourceNote);
      toast.success(`${figureWord} ${fig.number} uploaded`);
      setCaption('');
      setSourceNote('');
      load();
      onAssetsChanged?.();
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleInsert = async (fig) => {
    try {
      const res = await thesisApi.insertFigure(userId, fig.id, targetFor(fig.id) || null);
      toast.success(res.mode === 'new' ? 'Inserted into a new "Figures" section' : 'Figure inserted into the section');
      onInserted?.(res.chapter_id);
      onAssetsChanged?.();
    } catch (err) {
      toast.error('Insert failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleInsertRef = async (fig) => {
    const chapterId = targetFor(fig.id);
    if (!chapterId) { toast.error('Pick the section that should get the reference'); return; }
    try {
      await thesisApi.insertFigureReference(userId, fig.id, chapterId);
      toast.success(`Reference to ${figureWord} ${fig.number} added — it renumbers itself`);
      onInserted?.(chapterId);
    } catch (err) {
      toast.error('Insert failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleMove = async (fig, direction) => {
    try {
      await thesisApi.moveFigure(userId, fig.id, direction);
      await load();
      onAssetsChanged?.();
    } catch (err) {
      toast.error('Reorder failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (fig) => {
    if (!window.confirm(`Delete ${figureWord} ${fig.number}? The remaining figures renumber automatically.`)) return;
    try {
      await thesisApi.deleteFigure(userId, fig.id);
      toast.success('Figure deleted');
      load();
      onAssetsChanged?.();
    } catch (err) {
      toast.error('Delete failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <Card data-testid="figures-panel">
      <CardHeader>
        <CardTitle className="font-serif flex flex-wrap items-center gap-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <ImageIcon className="w-5 h-5 text-[#1d4ed8]" /> Figures
          <Badge variant="outline" className="text-[10px]">self-renumbering</Badge>
        </CardTitle>
        <CardDescription>
          Upload images (PNG, JPG, WebP, GIF, SVG) with a caption. Captions and in-text references
          store a stable marker, so reordering (↑ ↓) or deleting a figure renumbers every mention
          across the manuscript and in the Word / PDF / HTML exports.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={caption} onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption, e.g. CONSORT flow of participants"
            data-testid="figure-caption-input" />
          <Input value={sourceNote} onChange={(e) => setSourceNote(e.target.value)}
            placeholder="Source / note (optional), e.g. Adapted from [12]"
            data-testid="figure-source-input" />
        </div>
        <label className="cursor-pointer block">
          <input type="file" accept=".png,.jpg,.jpeg,.webp,.gif,.svg" className="hidden"
            onChange={handleUpload} disabled={uploading} data-testid="figure-upload-input" />
          <Button asChild disabled={uploading} className="w-full bg-[#0b1a35] hover:bg-[#1d4ed8] text-white">
            <span>{uploading
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
              : <><Upload className="w-4 h-4 mr-2" />Upload figure</>}</span>
          </Button>
        </label>

        {figures.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6" data-testid="figures-empty">
            No figures yet.
          </p>
        ) : (
          <div className="space-y-3" data-testid="figures-list">
            {figures.map((f, i) => (
              <div key={f.id} className="flex flex-col sm:flex-row gap-3 p-3 rounded-lg border border-border min-w-0"
                data-testid={`figure-row-${f.id}`}>
                <img src={`${BACKEND_URL}${f.url}`} alt={f.caption || f.filename}
                  className="w-full sm:w-32 h-24 object-contain bg-muted/40 rounded shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="text-sm font-medium">
                    {figureWord} {f.number}. <span className="font-normal">{f.caption || <span className="text-muted-foreground italic">no caption</span>}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {f.filename} · {Math.round((f.size || 0) / 1024)} KB
                    {f.kind === 'chart' && <Badge variant="secondary" className="ml-2 text-[10px]">generated chart</Badge>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      className="h-8 rounded-md border border-input bg-background px-2 text-xs min-w-[140px]"
                      value={targetFor(f.id)}
                      onChange={(e) => pickTarget(f.id, e.target.value)}
                      data-testid={`figure-target-${f.id}`}>
                      <option value="">New "Figures" section</option>
                      {chapters.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                    <Button size="sm" variant="outline" onClick={() => handleInsert(f)}
                      data-testid={`figure-insert-${f.id}`}>
                      <FilePlus2 className="w-3.5 h-3.5 mr-1.5" /> Insert figure
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleInsertRef(f)}
                      title="Add an in-text reference that renumbers itself"
                      data-testid={`figure-insert-ref-${f.id}`}>
                      <Quote className="w-3.5 h-3.5 mr-1.5" /> Insert reference
                    </Button>
                    <Button size="sm" variant="ghost" disabled={i === 0}
                      onClick={() => handleMove(f, 'up')} data-testid={`figure-up-${f.id}`}>
                      <ArrowUp className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" disabled={i === figures.length - 1}
                      onClick={() => handleMove(f, 'down')} data-testid={`figure-down-${f.id}`}>
                      <ArrowDown className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(f)}
                      data-testid={`figure-delete-${f.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
